import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import { toast } from "react-toastify";

import { WALLET_MINIMUM_AMOUNT } from "../../../common/constants";
import { formatNumber } from "../../../utils/convertToNumber";
import Loading from "../../Loading";
import { paymentMethods } from "../../../utils/paymentMethods";
import ButtonPrimary from "../../Button/ButtonPrimary";
import { ExchangeAcceptNDCOrder } from "../Services/NDCApi";
import { NDCExchangeAcceptReq } from "../NDCRequest/NDCRequest";

const NDCExchangePayment = () => {
  const { enterprise } = useSelector((state) => state.enterprise);
  const [loading, setLoading] = useState(false);

  const [paymentMethod, setPaymentMethod] = useState("wallet");

  const navigate = useNavigate();
  const location = useLocation();

  const { exchangeRequote, booking, exchangeSc } = location.state;

  const flightInfo = booking?.flight;
  const company = booking?.company;

  const { requotedOffer, dataMap } = exchangeRequote;

  const { totalTax, currency: taxCurrency } =
    requotedOffer?.addOfferItems?.reduce(
      (acc, item) => {
        const itemTax =
          item.requoteDiffPriceAndFareDetails?.diff?.price?.taxSummaries?.reduce(
            (taxAcc, tax) => taxAcc + +tax.totalTaxAmount.units,
            0
          ) || 0;
        const currency =
          item.requoteDiffPriceAndFareDetails?.diff?.price?.taxSummaries?.[0]
            ?.totalTaxAmount?.currencyCode;
        return {
          totalTax: acc.totalTax + itemTax,
          currency: currency || acc.currency,
        };
      },
      { totalTax: 0, currency: "" }
    );

  const { totalFees, currency: feeCurrency } =
    requotedOffer?.addOfferItems?.reduce(
      (acc, item) => {
        const itemFee =
          +item?.requoteDiffPriceAndFareDetails.diff.price.totalAmount.units ||
          0;
        const currency =
          item.requoteDiffPriceAndFareDetails?.diff?.price?.totalAmount
            ?.currencyCode;
        return {
          totalFees: acc.totalFees + itemFee,
          currency: currency || acc.currency,
        };
      },
      { totalFees: 0, currency: "" }
    );

  const getPenalty = (ref) => {
    return dataMap?.penalties?.[ref]?.penaltyPrice?.totalAmount;
  };

  const { totalPenalty, currency: penaltyCurrency } =
    requotedOffer?.addOfferItems?.reduce(
      (acc, item) => {
        const itemPenalty = +getPenalty(item?.penaltyRefId)?.units || 0;
        const currency = getPenalty(item?.penaltyRefId)?.currencyCode;
        return {
          totalPenalty: acc.totalPenalty + itemPenalty,
          currency: currency || acc.currency,
        };
      },
      { totalPenalty: 0, currency: "" }
    );

  const { fareDiff, currency: fareCurrency } =
    requotedOffer?.addOfferItems?.reduce(
      (acc, item) => {
        const itemFareDiff =
          +item.requoteDiffPriceAndFareDetails?.new?.price?.totalAmount.units -
          +item.requoteDiffPriceAndFareDetails?.old?.price?.totalAmount.units;
        const currency =
          item.requoteDiffPriceAndFareDetails?.new?.price?.totalAmount
            .currencyCode;
        return {
          fareDiff: acc.fareDiff + itemFareDiff,
          currency: currency || acc.currency,
        };
      },
      { fareDiff: 0, currency: "" }
    );

  const handleClick = async () => {
    const now = moment();
    const start = moment(
      exchangeRequote?.requotedOffer?.offerExpirationDateTime
    );

    if (company) {
      if (
        company.wallet <
        (company?.ADMLimit || WALLET_MINIMUM_AMOUNT) + totalFees
      ) {
        toast.error("Insufficient balance");
        return;
      }
    }

    const isCreditTimeRestricted = now.isAfter(start);

    if (isCreditTimeRestricted) {
      toast.error("Offer expired, Please Try Again.");
      navigate(-1);
    }

    const data = {
      ...flightInfo,
      channel: "NDC",
      details: {
        paymentMethod: paymentMethod,
        penalty: totalPenalty,
        exchangeDiff: fareDiff,
        totalAmount: totalFees + (exchangeSc || 0),
      },
      amount: {
        currencyCode: feeCurrency,
        units: totalFees.toString(),
        // nanos: 0,
      },
    };

    const acceptReq = NDCExchangeAcceptReq(exchangeRequote, data);

    const res = await ExchangeAcceptNDCOrder(acceptReq, data, setLoading);

    console.log(acceptReq);

    if (res?.Errors?.Error || res?.error) {
      toast.error(
        `Error in exchange reshop ${
          res?.Errors?.Error[0]?.value || res?.error?.code
        }`
      );
      return;
    }
    toast.success("Exchange Successful");
    navigate("/booking");
  };

  function getTotalPrice() {
    const result = totalFees + (exchangeSc || 0);
    return formatNumber(result);
  }

  if (loading) return <Loading />;

  const renderSidebar = () => {
    const segments = Object.entries(dataMap.paxSegments);
    return (
      <div className="container flex flex-col px-5 mx-auto space-y-8 w-full shadow sm:rounded-2xl sm:border border-neutral-200 dark:border-neutral-700 sm:p-6 xl:p-8">
        <div className="flex flex-col sm:flex-row sm:items-center">
          <div className="py-1 space-y-3">
            {segments?.map(([segmentKey, segment]) => {
              return (
                <div key={segmentKey} className="flex flex-col space-y-2">
                  <span className="text-lg font-semibold text-neutral-500">
                    <h1> {segment.marketingInfo.carrierCode}</h1>
                  </span>
                  <span className="text-lg font-semibold text-neutral-500 line-clamp-1">
                    <h1>
                      {segment.dep.stationCode} - {segment.arrival.stationCode}
                    </h1>
                  </span>
                  <span className="block mt-1 text-base font-medium">
                    {moment(segment.dep.schedDateTime).format("MMM DD, YYYY ")}{" "}
                    -{" "}
                    {moment(segment.arrival.schedDateTime).format(
                      "MMM DD, YYYY "
                    )}
                  </span>
                  <span className="block mt-1 text-base font-medium">
                    {moment(segment.dep.schedDateTime).format("hh:mm A")} -{" "}
                    {moment(segment.arrival.schedDateTime).format("hh:mm A")}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
        <div className="flex flex-col space-y-4">
          <h3 className="text-2xl font-semibold">Fee detail</h3>
          <div className="flex justify-between text-neutral-6000 dark:text-neutral-300">
            <span>Fare Difference</span>
            <span>
              {fareCurrency} {formatNumber(fareDiff)}
            </span>
          </div>
          {/* <div className="flex justify-between text-neutral-6000 dark:text-neutral-300"> */}
          {/* <span>Total Taxes</span> */}
          {/* <span> */}
          {/* {taxCurrency} {totalTax} */}
          {/* </span> */}
          {/* </div> */}
          <div className="flex justify-between text-neutral-6000 dark:text-neutral-300">
            <span>Total Penalty</span>
            <span>
              {penaltyCurrency} {formatNumber(totalPenalty)}
            </span>
          </div>
          {/* <div className="flex justify-between text-neutral-6000 dark:text-neutral-300">
            <span>Service Charge</span>
            <span>
              {feeCurrency} {formatNumber(exchangeSc)}
            </span>
          </div> */}

          <div className="border-b border-neutral-200"></div>
          <div className="flex justify-between text-xl font-bold text-black">
            <span>Total Price</span>
            <span>
              {feeCurrency} {getTotalPrice()}
            </span>
          </div>
        </div>
      </div>
    );
  };

  function checkWalletBalance() {
    if (
      enterprise?.method !== "postPaid" &&
      (!company ||
        company.wallet > WALLET_MINIMUM_AMOUNT + totalFees + (exchangeSc || 0))
    ) {
      // show error message
      return true;
    }
  }

  function creditPass() {
    if (
      company &&
      company?.creditType !== "" &&
      company?.creditLimit > totalFees + (exchangeSc || 0)
    ) {
      return true;
    } else {
      return false;
    }
  }

  const renderMain = () => {
    return (
      <div className="container flex flex-col px-5 mx-auto space-y-8 w-full shadow sm:rounded-2xl sm:border border-neutral-200 dark:border-neutral-700 sm:p-6 xl:p-8">
        <h2 className="text-3xl font-semibold lg:text-4xl">
          Confirm and payment
        </h2>
        <div className="border-b border-neutral-200 dark:border-neutral-700"></div>

        <div className="flex justify-between">
          <h1 className="text-base font-medium">
            Offer Expire At:{" "}
            <span className="text-red-500">
              {moment(requotedOffer?.offerExpirationDateTime).format(
                "MMM DD, YYYY hh:mm A"
              )}
            </span>
          </h1>
        </div>

        <div className="">
          {checkWalletBalance() && (
            <button
              onClick={() => {
                setPaymentMethod("wallet");
              }}
              className={`p-8 rounded-lg focus:outline-none mr-10  ${
                paymentMethod === "wallet"
                  ? "bg-neutral-800  text-white  h-36 pt-4"
                  : "text-neutral-6000  border border-gray-800 shadow-xl h-36 pt-4"
              }`}
            >
              <div className="flex flex-col gap-2 items-center">
                <img
                  src={
                    "https://www.kindpng.com/picc/m/580-5809740_white-wallet-logo-png-wallet-icon-ios-transparent.png"
                  }
                  alt=""
                  className="my-2 w-16"
                />
                Wallet
              </div>
            </button>
          )}
          {creditPass() && (
            <button
              onClick={() => {
                setPaymentMethod("credit");
              }}
              className={`p-8 rounded-lg focus:outline-none mr-10  ${
                paymentMethod === "credit"
                  ? "bg-neutral-800  text-white  h-36 pt-4"
                  : "text-neutral-6000  border border-gray-800 shadow-xl h-36 pt-4"
              }`}
            >
              <div className="flex flex-col gap-2 items-center">
                <img
                  src={
                    "https://media.istockphoto.com/vectors/safe-payment-logo-template-designs-vector-illustration-vector-id1215256045?k=20&m=1215256045&s=612x612&w=0&h=IQX0a8uPVKSTGfJyWtWv3eXAMk4LTuQSmMtNqv_IBZs="
                  }
                  alt=""
                  className="my-2 w-16"
                />
                Credit
              </div>
            </button>
          )}
        </div>

        <h1 className="text-xl font-semibold">
          Selected Payment Method:{" "}
          <span className="text-main">
            {
              paymentMethods.find((method) => method.name === paymentMethod)
                ?.title
            }
          </span>
        </h1>

        {(paymentMethod === "wallet" || paymentMethod === "credit") && (
          <>
            <div className="space-y-2 lg:hidden row lg:p-0">
              <div className="col-12">
                <ButtonPrimary className="w-full text-black rounded-3xl border-red-500 bg-slate-50 border-1 hover:text-white hover:bg-red-400 focus:outline-none focus:ring-2 focus:ring-main focus:ring-opacity-50">
                  Cancel Booking
                </ButtonPrimary>
              </div>
              <div className="col-12">
                <ButtonPrimary
                  onClick={handleClick}
                  className="w-full text-black rounded-3xl border-1 border-main bg-slate-50 hover:text-white hover:bg-main-900 focus:outline-none focus:ring-2 focus:ring-main focus:ring-opacity-50"
                >
                  Confirm and pay
                </ButtonPrimary>
              </div>
            </div>

            <div className="hidden gap-4 justify-between pt-8 lg:flex">
              <div className="w-full">
                <ButtonPrimary className="w-full text-black rounded-3xl border-red-500 bg-slate-50 border-1 hover:text-white hover:bg-red-400 focus:outline-none focus:ring-2 focus:ring-main focus:ring-opacity-50">
                  Cancel Booking
                </ButtonPrimary>
              </div>
              <div className="w-full">
                <ButtonPrimary
                  onClick={handleClick}
                  className="w-full text-black rounded-3xl border-1 border-main bg-slate-50 hover:text-white hover:bg-main-900 focus:outline-none focus:ring-2 focus:ring-main focus:ring-opacity-50"
                >
                  Confirm and pay
                </ButtonPrimary>
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div>
      <main className="container flex flex-col-reverse mt-11 mb-24 lg:mb-32 lg:flex-row">
        <div className="w-full lg:w-3/5 xl:w-2/3 lg:pr-10">{renderMain()}</div>
        <div className="flex-grow mb-10 lg:block">{renderSidebar()}</div>
      </main>
    </div>
  );
};

export default NDCExchangePayment;
